import { useEffect, useMemo, useRef, useState } from "react"
import { Head, Link, router, usePage } from "@inertiajs/react"
import {
  Bot,
  Calendar as CalendarIcon,
  ChevronDown,
  ChevronRight,
  ExternalLink,
  Plus,
  Upload as UploadIcon,
  User as UserIcon,
} from "lucide-react"
import { ActivityComposer } from "@/components/activity/ActivityComposer"
import { ActivityFeed } from "@/components/activity/ActivityFeed"
import type { ActivityEvent } from "@/components/activity/types"
import { AppShell } from "@/components/AppShell"
import { TagPicker, type TagSelection } from "@/components/tags/TagPicker"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { AutoGrowTextarea } from "@/components/ui/auto-grow-textarea"
import { Select } from "@/components/ui/select"
import { RichTextField } from "@/components/ui/rich-text-field"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { useFormatDateTime } from "@/lib/datetime"
import { isSchedulePastDue } from "@/lib/scheduling"
import { PastDueWarning } from "@/components/scheduling/PastDueWarning"
import { ChannelIcon } from "@/lib/channel-icons"
import { channelColor } from "@/lib/channel-colors"
import { cn } from "@/lib/utils"
import { useAutosave, useFieldSaveIndicator } from "@/lib/use-autosave"
import { SavedField } from "@/components/SavedCheck"
import { PieceSubNav, type PieceTabKey } from "./PieceSubNav"
import { AttachmentList } from "@/components/attachments/AttachmentList"
import { FileUploadButton } from "@/components/attachments/FileUploadButton"
import { AttachmentViewer } from "@/components/attachments/AttachmentViewer"
import { useActionMenu, type ActionItem, type ActionKind } from "@/lib/use-action-menu"
import { useFireAction } from "@/lib/use-fire-action"
import { todoVerb } from "@/lib/todo-labels"

import type { PageProps } from "@/types/inertia"

type FieldSchema = {
  name: string
  label: string
  kind: "text" | "markdown" | "boolean" | "slug" | "images" | "video" | "featured_image"
  required: boolean
  multiline?: boolean
  options?: string[]
}

type Piece = {
  id: number
  name: string
  channel: string
  fields: Record<string, unknown>
  display_title: string | null
  publishing_status: string
  published_url: string | null
  published_at: string | null
  archived_at: string | null
  prior_publishing_status: string | null
  failed_reason: string | null
  publish_at: string | null
  slot: {
    publish_at: string
    upload_at: string | null
    use_platform_scheduling: boolean
    manual_publish: boolean
    publishing_at: string | null
  } | null
  created_at: string
  events: ActivityEvent[]
  events_comment_count: number
  attachments: Array<{ id: number; kind: string; filename: string | null; url: string | null; content_type: string | null; bytes: number | null; position: number }>
  open_todos: Array<{ assignee: string; kind: string }>
  last_activity: { label: string; at: string; actor: "user" | "agent" }
  tags: Array<{ id: number; name: string; color: string; icon: string }>
}

type Channel = {
  slug: string
  name: string
  channel_type: string
  color: string
  character_limit: number | null
  fields_schema: FieldSchema[]
  instructions: string | null
  publishing_account_label: string | null
  credentials_status: string
  publishes_manually?: boolean
  allow_platform_scheduling?: boolean
  platform_scheduling_lead_hours?: number
  default_to_platform_scheduling?: boolean
  supports_platform_scheduling?: boolean
  forces_platform_scheduling?: boolean
}

type Props = {
  piece: Piece
  channel: Channel
  tab: PieceTabKey
}

// Post-dispatch terminal states — the piece has either shipped or been
// taken out of the pipeline entirely, so its content is frozen.
const LOCKED_STATUSES = ["published", "archived"]

// Whether the operator can still edit (and autosave) this piece's content.
// Content stays editable for the whole pre-dispatch window — pending,
// queued_to_schedule, scheduled, failed, backburner — so a cued or
// scheduled piece can keep being tweaked right up until it ships. Editing
// locks once the piece reaches a terminal state, or once the publishing
// job has entered its kill window (slot.publishing_at set — the same
// `not_publishing_in_flight?` boundary the model uses to refuse
// unschedule / cancel / backburner). Past that point the post may already
// be live, so a silent overwrite would race the publishing job.
function isContentEditable(piece: Piece): boolean {
  if (LOCKED_STATUSES.includes(piece.publishing_status)) return false
  if (piece.slot?.publishing_at != null) return false
  return true
}

// Single-piece detail view. Two tabs — Content / Activity — served by the
// same component with `tab` coming in via the route. The page header
// carries the piece's internal name (click to edit inline) and its tags.
export default function PieceShow({ piece, channel, tab }: Props) {
  const { props } = usePage<PageProps>()
  const errors = props.errors ?? {}

  // Local form state — flushed to the server via Save / action buttons.
  //
  // All editable content lives in a single `fields` hash whose keys are
  // determined by the channel's fields_schema. The renderer below maps
  // each schema entry to the appropriate input. Attachment fields
  // (images/video/featured_image) are NOT held here — they live in the
  // attachments table and edit independently via FileUploadButton.
  const [fields, setFields]             = useState<Record<string, unknown>>(piece.fields ?? {})

  // Inertia reuses this page component instance across piece→piece
  // navigations (same component name → no unmount), so the useState
  // initializers above run only on the FIRST piece viewed. Without an
  // explicit reset, the previous piece's `fields` (and comment draft) bleed
  // into the next piece — most visibly, the markdown body editor, which
  // captures its value at mount and is remounted by the per-piece `key` in
  // FieldRenderer, would re-mount showing the prior piece's body even though
  // the server stored an empty one. Reset the editor state during render
  // whenever the piece identity changes — the React-idiomatic "adjust state
  // on a prop change" pattern, which resets synchronously with no stale
  // flash. https://react.dev/learn/you-might-not-need-an-effect#adjusting-some-state-when-a-prop-changes
  const [renderedPieceId, setRenderedPieceId] = useState(piece.id)
  if (piece.id !== renderedPieceId) {
    setRenderedPieceId(piece.id)
    setFields(piece.fields ?? {})
  }

  const setField = (name: string, value: unknown) => {
    setFields((prev) => ({ ...prev, [name]: value }))
  }

  const collectedAttrs = useMemo(
    () => ({
      // Name is owned by the inline-edit header — it PATCHes independently
      // so we don't include it in the action payloads (would risk
      // round-tripping a stale value).
      fields,
    }),
    [fields],
  )

  const saveFieldsOnly = () => {
    router.patch(
      `/pieces/${piece.id}`,
      { piece: collectedAttrs } as never,
      { preserveScroll: true, preserveState: true },
    )
  }

  const saveIndicator = useFieldSaveIndicator()

  // Content stays editable right up until the piece is actually dispatched
  // to the platform — the operator can keep tweaking a queued_to_schedule
  // or scheduled piece until it ships. Editing is only locked once the
  // piece is in a post-dispatch terminal state (published / archived) or
  // inside the publishing kill window (slot.publishing_at set — the same
  // `not_publishing_in_flight?` boundary the model uses to refuse
  // unschedule / backburner). Mirrors ContentTab's gate so
  // the inputs go read-only at the same moment autosave stops.
  const contentEditable = isContentEditable(piece)

  // Debounced autosave for all editor fields. Fires ~600ms after the user
  // pauses typing. Enabled for the whole pre-dispatch window so edits to a
  // cued/scheduled piece persist; disabled once the piece has shipped or is
  // mid-publish, where a silent overwrite would race the publishing job.
  useAutosave(collectedAttrs, saveFieldsOnly, {
    enabled: contentEditable,
    onSaved: saveIndicator.markSaved,
  })

  return (
    <>
      <Head title={piece.name}>
        <meta name="description" content={`Detail view for "${piece.name}".`} />
        <meta property="og:title" content={piece.name} />
        <meta property="og:description" content={`Detail view for "${piece.name}".`} />
      </Head>

      <AppShell wide="medium">
      {/* Channel-tinted theme wrapper. Re-binds the accent CSS vars to
          this channel's hue, so every `bg-accent` / `text-accent` /
          `border-accent` / *-accent-faded / *-accent-display utility
          inside the piece page (tabs, links, buttons, action box, etc.)
          renders in the channel's color. */}
      <div className={channelColor(channel.color).theme}>
        <div className="border-b border-hairline pb-6">
          <div className="flex flex-wrap gap-6">
            <div className="min-w-0 flex-1">
              {/* key by piece id so an in-progress inline rename never
                  carries over to the next piece on Inertia navigation. */}
              <PieceNameHeader key={piece.id} piece={piece} />
              <PieceTags piece={piece} />
            </div>

            <NextActionBox piece={piece} channel={channel} />
          </div>
        </div>

        <PieceSubNav
          pieceId={piece.id}
          active={tab}
          commentCount={piece.events_comment_count}
        />

        {errors.base && (
          <p className="mt-6 rounded-md bg-danger-faded px-3 py-2 text-sm text-danger-display">
            {errors.base}
          </p>
        )}

        <div className="mt-8">
          {tab === "content" && (
            <ContentTab
              piece={piece}
              channel={channel}
              fields={fields}
              setField={setField}
              editable={contentEditable}
              savedKey={saveIndicator.savedKey}
              markEdited={saveIndicator.markEdited}
            />
          )}
          {tab === "activity" && <ActivityTab piece={piece} />}
        </div>
      </div>
      </AppShell>
    </>
  )
}

// --- Header ------------------------------------------------------------

// Tags auto-save on every change. The picker's value is derived straight
// from the piece prop, so a successful PATCH (which reloads props) is what
// re-renders the selected set — any just-created tag comes back as an
// existing one, so there's nothing to reconcile locally.
function PieceTags({ piece }: { piece: Piece }) {
  const { props } = usePage<PageProps>()
  const available = props.new_piece_options?.tags ?? []

  const onTagsChange = (next: TagSelection) => {
    router.patch(
      `/pieces/${piece.id}`,
      { tag_ids: next.tagIds, new_tags: next.newTags },
      { preserveScroll: true },
    )
  }
  const selection: TagSelection = { tagIds: piece.tags.map((t) => t.id), newTags: [] }

  return (
    <div className="mt-3 max-w-md">
      <TagPicker available={available} value={selection} onChange={onTagsChange} />
    </div>
  )
}

// Inline-editable piece name. Default render is an <h1>; clicking swaps
// it for an Input that PATCHes on blur or Enter. Escape cancels. The
// name is independent of the rest of the editor's local state — saving
// it doesn't fire any AASM event.
function PieceNameHeader({ piece }: { piece: Piece }) {
  const [editing, setEditing] = useState(false)
  const [value, setValue]     = useState(piece.name)
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (editing) inputRef.current?.select()
  }, [editing])

  const save = () => {
    const next = value.trim()
    if (!next || next === piece.name) {
      setValue(piece.name)
      setEditing(false)
      return
    }
    router.patch(
      `/pieces/${piece.id}`,
      { piece: { name: next } },
      {
        preserveScroll: true,
        onSuccess: () => setEditing(false),
        onError: () => {
          setValue(piece.name)
          setEditing(false)
        },
      },
    )
  }

  if (editing) {
    return (
      <Input
        ref={inputRef}
        autoFocus
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onBlur={save}
        onKeyDown={(e) => {
          if (e.key === "Enter") save()
          if (e.key === "Escape") {
            setValue(piece.name)
            setEditing(false)
          }
        }}
        className="h-auto text-3xl font-display font-semibold leading-tight"
      />
    )
  }

  return (
    <h1
      onClick={() => setEditing(true)}
      className="cursor-text hover:text-accent-display"
      title="Click to rename"
    >
      {piece.name}
    </h1>
  )
}

// Accent-faded info box pinned to the right of the page header. Two
// zones — a small "Last activity" line at the top, and a single big
// affordance below it that adapts to the piece's lifecycle stage:
//
//   • pending / failed / queued_to_schedule with an agent-owned todo
//     → non-clickable indicator describing what the agent is queued to do.
//   • scheduled → clickable card with the slot date/time, links to the
//     unified /calendar view.
//   • published → clickable card with the publish date/time, opens the
//     live URL in a new tab.
function NextActionBox({
  piece,
  channel,
}: {
  piece: Piece
  channel: Channel
}) {
  const fmt = useFormatDateTime()
  const [activityOpen, setActivityOpen] = useState(false)

  const agentTodos = piece.open_todos.filter((t) => t.assignee === "agent")
  const channelForMenu = {
    slug:                            channel.slug,
    channel_type:                    channel.channel_type,
    credentials_status:              channel.credentials_status,
    publishes_manually:              channel.publishes_manually,
    allow_platform_scheduling:       channel.allow_platform_scheduling,
    platform_scheduling_lead_hours:  channel.platform_scheduling_lead_hours,
    default_to_platform_scheduling:  channel.default_to_platform_scheduling,
    supports_platform_scheduling:    channel.supports_platform_scheduling,
    forces_platform_scheduling:      channel.forces_platform_scheduling,
  }
  const menu = useActionMenu(piece, channelForMenu)
  const { fire, dialogs } = useFireAction(piece, channelForMenu)

  return (
    <aside className="flex w-full shrink-0 flex-col gap-3 rounded-md bg-accent-faded p-4 text-sm sm:w-64">
      {/* Collapsible Last-activity */}
      <button
        type="button"
        onClick={() => setActivityOpen((v) => !v)}
        className="flex w-full items-center justify-between gap-2 text-left text-xs text-ink-muted hover:text-ink-display"
      >
        <span className="flex items-center gap-1.5">
          {piece.last_activity.actor === "agent" ? (
            <Bot className="h-3.5 w-3.5" aria-label="Agent" />
          ) : (
            <UserIcon className="h-3.5 w-3.5" aria-label="You" />
          )}
          Last activity · {fmt(piece.last_activity.at)}
        </span>
        {activityOpen ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
      </button>
      {activityOpen && (
        <p className="-mt-1 rounded-md border border-hairline bg-surface px-3 py-2 text-ink-display">{piece.last_activity.label}</p>
      )}

      {/* Agent todos panel — only when there are open agent todos */}
      {agentTodos.length > 0 && (
        <div className="flex flex-col gap-2 rounded-md border border-hairline bg-surface p-3">
          <div className="flex items-center gap-1.5 text-xs text-ink-muted">
            <Bot className="h-3.5 w-3.5" />
            {agentTodos.length} Agent {agentTodos.length === 1 ? "todo" : "todos"}
          </div>
          <ol className="flex flex-col gap-1">
            {agentTodos.map((t, idx) => (
              <li key={t.kind} className="flex items-center gap-2 text-sm text-ink-display">
                <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-accent-faded text-xs font-medium text-accent-display">
                  {idx + 1}
                </span>
                <span>{capitalize(todoVerb(t.kind))}</span>
              </li>
            ))}
          </ol>
        </div>
      )}

      {/* Status-specific informational chrome (slot, published URL, etc.) */}
      <StatusChrome piece={piece} channel={channel} fmt={fmt} />

      {/* Full-width button-dropdown driven by useActionMenu */}
      {menu.primary && (
        <FullWidthActionMenu primary={menu.primary} secondary={menu.secondary} onPick={fire} />
      )}

      {dialogs}
    </aside>
  )
}

function capitalize(s: string): string {
  return s.length > 0 ? s[0].toUpperCase() + s.slice(1) : s
}

// Compact status block above the action button — surfaces the slot time
// for scheduled pieces, the published URL for published pieces, etc.
function StatusChrome({
  piece,
  channel,
  fmt,
}: {
  piece: Piece
  channel: Channel
  fmt: ReturnType<typeof useFormatDateTime>
}) {
  const labelClass = "inline-flex items-center gap-1.5 text-xs text-ink-muted"
  const valueClass = "text-base font-medium text-ink-display"

  if (piece.publishing_status === "published" && piece.published_url) {
    return (
      <a
        href={piece.published_url}
        target="_blank"
        rel="noreferrer"
        className="flex flex-col gap-1 rounded-md border border-hairline bg-surface p-3 no-underline hover:border-accent hover:bg-accent-faded/50"
      >
        <span className={labelClass}>
          <ExternalLink className="h-3.5 w-3.5" /> Published
        </span>
        <span className={valueClass}>{fmt(piece.published_at ?? piece.last_activity.at)}</span>
        <span className={labelClass}>
          <ChannelIcon channelType={channel.channel_type} color={channel.color} className="h-3 w-3" />
          View live on {channel.name}
        </span>
      </a>
    )
  }

  if (piece.publishing_status === "scheduled" && piece.slot) {
    const isManual = piece.slot.manual_publish
    const isPlatform = piece.slot.use_platform_scheduling
    return (
      <Link
        href="/calendar"
        className="flex flex-col gap-1 rounded-md border border-hairline bg-surface p-3 no-underline hover:border-accent hover:bg-accent-faded/50"
      >
        {isPlatform && piece.slot.upload_at && (
          <>
            <span className={labelClass}>
              <UploadIcon className="h-3.5 w-3.5" /> Upload
            </span>
            <span className={valueClass}>{fmt(piece.slot.upload_at)}</span>
          </>
        )}
        <span className={labelClass}>
          <CalendarIcon className="h-3.5 w-3.5" />{" "}
          {isPlatform ? "Publish" : "Scheduled"}{isManual && " (manual)"}
        </span>
        <span className={valueClass}>{fmt(piece.slot.publish_at)}</span>
        {isSchedulePastDue(piece.publishing_status, piece.slot.publish_at) && (
          <PastDueWarning
            message="This missed its scheduled time and needs rescheduling."
            className="mt-1 text-xs"
          />
        )}
        {channel.publishing_account_label && (
          <span className={labelClass}>
            <ChannelIcon channelType={channel.channel_type} color={channel.color} className="h-3 w-3" />
            {channel.publishing_account_label}
          </span>
        )}
      </Link>
    )
  }

  if (piece.publishing_status === "queued_to_schedule") {
    return (
      <div className="flex flex-col gap-1 rounded-md border border-hairline bg-surface p-3">
        <span className={labelClass}>
          <CalendarIcon className="h-3.5 w-3.5" /> Queued to schedule
        </span>
        {channel.publishing_account_label && (
          <span className={labelClass}>
            <ChannelIcon channelType={channel.channel_type} color={channel.color} className="h-3 w-3" />
            {channel.publishing_account_label}
          </span>
        )}
      </div>
    )
  }

  if (piece.publishing_status === "queued_to_publish") {
    return (
      <div className="flex flex-col gap-1 rounded-md border border-hairline bg-surface p-3">
        <span className={labelClass}>
          <CalendarIcon className="h-3.5 w-3.5" /> To publish manually
        </span>
        <span className={labelClass}>
          Schedule or publish this on {channel.name} by hand, then mark it scheduled or published.
        </span>
        {channel.publishing_account_label && (
          <span className={labelClass}>
            <ChannelIcon channelType={channel.channel_type} color={channel.color} className="h-3 w-3" />
            {channel.publishing_account_label}
          </span>
        )}
      </div>
    )
  }

  if (piece.publishing_status === "backburner") {
    return (
      <div className="flex flex-col gap-1 rounded-md border border-hairline bg-surface p-3">
        <span className={labelClass}>On the backburner</span>
        <span className={labelClass}>No schedule, no agent todos.</span>
      </div>
    )
  }

  if (piece.publishing_status === "archived") {
    return (
      <div className="flex flex-col gap-1 rounded-md border border-hairline bg-surface p-3">
        <span className={labelClass}>Archived</span>
      </div>
    )
  }

  return null
}

// Full-width primary + chevron menu. Renders the primary action as a
// stretched Button and tucks the remaining actions inside a dropdown
// triggered by a chevron flush to the right of the primary.
function FullWidthActionMenu({
  primary,
  secondary,
  onPick,
}: {
  primary: ActionItem
  secondary: ActionItem[]
  onPick: (kind: ActionKind) => void
}) {
  return (
    <div className="flex w-full">
      <Button
        type="button"
        variant={primary.destructive ? "danger" : "primary"}
        size="md"
        className="flex-1 rounded-r-none"
        onClick={() => onPick(primary.kind)}
      >
        {primary.label}
      </Button>
      {secondary.length > 0 && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              type="button"
              variant={primary.destructive ? "danger" : "primary"}
              size="md"
              className="rounded-l-none border-l border-l-page/20 px-2"
              aria-label="More actions"
            >
              <ChevronDown className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="min-w-[14rem]">
            {secondary.map((item) => (
              <DropdownMenuItem
                key={item.kind}
                onSelect={(e) => {
                  e.preventDefault()
                  onPick(item.kind)
                }}
                className={item.destructive ? "text-danger-display" : undefined}
              >
                {item.label}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </div>
  )
}

// --- Tab: Content ------------------------------------------------------

function ContentTab({
  piece,
  channel,
  fields,
  setField,
  editable,
  savedKey,
  markEdited,
}: {
  piece: Piece
  channel: Channel
  fields: Record<string, unknown>
  setField: (name: string, value: unknown) => void
  editable: boolean
  savedKey: string | null
  markEdited: (key: string) => void
}) {
  // Pieces show every field defined for their channel type, in schema
  // order. The renderer dispatches on `kind`; field state lives in the
  // parent `fields` hash.
  const allowedAttachmentKinds: Array<"image" | "video"> = channel.fields_schema
    .filter((f) => f.kind === "images" || f.kind === "video")
    .map((f) => (f.kind === "images" ? "image" : "video"))

  // Newsletter pieces collapse the three promo_* fields
  // (promo_headline, promo_content, promo_skip) into one composite
  // dropdown widget — Default / Custom / Don't show. The underlying
  // schema + storage stay the same; only the editor surface groups
  // them. Render the composite at the first promo_* field's position
  // and skip the rest individually.
  const isNewsletter      = channel.channel_type === "newsletter"
  const promoFieldNames   = ["promo_headline", "promo_content", "promo_skip"]
  const firstPromoFieldAt = isNewsletter
    ? channel.fields_schema.findIndex((f) => promoFieldNames.includes(f.name))
    : -1

  return (
    <div>
      {!editable && <LockedContentNotice piece={piece} />}

      <div className="space-y-6">
        {channel.fields_schema.map((field, idx) => {
          if (isNewsletter && promoFieldNames.includes(field.name)) {
            if (idx !== firstPromoFieldAt) return null
            return (
              <NewsletterPromoBox
                key="promo-box"
                pieceId={piece.id}
                fields={fields}
                setField={(name, value) => { setField(name, value); markEdited(name) }}
                editable={editable}
                savedKey={savedKey}
              />
            )
          }
          return (
            <FieldRenderer
              key={field.name}
              field={field}
              piece={piece}
              channel={channel}
              value={fields[field.name]}
              onChange={(v) => { setField(field.name, v); markEdited(field.name) }}
              editable={editable}
              savedKey={savedKey}
              allowedAttachmentKinds={allowedAttachmentKinds}
            />
          )
        })}
      </div>
    </div>
  )
}

// One row per schema field. Dispatches on `kind`. Attachment-kind fields
// don't have a value in `fields` — they render from `piece.attachments`
// and edit via FileUploadButton (which POSTs to the attachments
// endpoint directly, side-stepping the form state). Text/markdown/slug/
// boolean fields read+write their value in the parent fields hash.
function FieldRenderer({
  field,
  piece,
  channel,
  value,
  onChange,
  editable,
  savedKey,
  allowedAttachmentKinds,
}: {
  field: FieldSchema
  piece: Piece
  channel: Channel
  value: unknown
  onChange: (next: unknown) => void
  editable: boolean
  savedKey: string | null
  allowedAttachmentKinds: Array<"image" | "video">
}) {
  const id    = `pe-${field.name}`
  const label = (
    <label htmlFor={id}>
      {field.label}
      {field.required && <span className="ml-1 text-danger-display" aria-label="required">*</span>}
    </label>
  )

  // ---------- Attachment kinds ----------
  if (field.kind === "featured_image") {
    const current = piece.attachments.find((a) => a.kind === "featured_image")
    return (
      <div className="space-y-2">
        {label}
        {current ? (
          <FeaturedImagePreview pieceId={piece.id} attachment={current} editable={editable} />
        ) : (
          <p className="text-sm text-ink-muted">No featured image yet.</p>
        )}
        {editable && (
          <FileUploadButton
            pieceId={piece.id}
            kind="featured_image"
            label={current ? "Replace featured image" : "Pick featured image"}
          />
        )}
      </div>
    )
  }

  if (field.kind === "images" || field.kind === "video") {
    // Render the unified attachments list once for the schema's first
    // attachment field, skip subsequent ones — it covers all allowed kinds.
    const firstAttachmentField = channel.fields_schema.find(
      (f) => f.kind === "images" || f.kind === "video",
    )
    if (firstAttachmentField?.name !== field.name) return null

    const items = piece.attachments
      .filter((a) => a.kind === "image" || a.kind === "video") as Array<{
        id: number; kind: "image" | "video"; filename: string | null; url: string | null; content_type: string | null; bytes: number | null; position: number
      }>
    return (
      <AttachmentList
        pieceId={piece.id}
        attachments={items}
        allowedKinds={allowedAttachmentKinds}
        editable={editable}
      />
    )
  }

  // ---------- Boolean ----------
  if (field.kind === "boolean") {
    const checked = value === true
    return (
      <div className="space-y-2">
        <label
          htmlFor={id}
          className={cn(
            "flex items-center gap-2 font-normal text-ink-body",
            editable ? "cursor-pointer" : "cursor-default",
          )}
        >
          <input
            id={id}
            type="checkbox"
            checked={checked}
            onChange={(e) => onChange(e.target.checked)}
            disabled={!editable}
            className="h-4 w-4"
          />
          {field.label}
        </label>
      </div>
    )
  }

  // ---------- Markdown ----------
  if (field.kind === "markdown") {
    return (
      <div className="space-y-2">
        {label}
        <SavedField visible={savedKey === field.name}>
          <RichTextField
            key={`${field.name}-${piece.id}`}
            defaultValue={(value as string) ?? ""}
            onChange={(md) => onChange(md)}
            readOnly={!editable}
            placeholder={`Write the ${field.label.toLowerCase()} in markdown…`}
            className="min-h-72"
          />
        </SavedField>
      </div>
    )
  }

  // ---------- Slug ----------
  if (field.kind === "slug") {
    return (
      <div className="space-y-2">
        {label}
        <SavedField visible={savedKey === field.name}>
          <Input
            id={id}
            value={(value as string) ?? ""}
            onChange={(e) => onChange(e.target.value)}
            readOnly={!editable}
            placeholder="Leave blank to auto-derive from the title"
          />
        </SavedField>
        <p className="text-xs text-ink-muted">
          Saved as lowercase with non-alphanumerics replaced by hyphens.
        </p>
      </div>
    )
  }

  // ---------- Text (select / multiline / single-line) ----------
  if (field.options && field.options.length > 0) {
    // YouTube's status.publishAt API only schedules private → public flips.
    // When the piece is scheduled with platform scheduling, lock the
    // visibility select to "public" so the operator can't pick a value
    // YouTube will reject at upload time.
    const isYoutubeChannel =
      channel.channel_type === "youtube_shorts" || channel.channel_type === "youtube_long_form"
    const isVisibility = field.name === "visibility" && isYoutubeChannel
    const platformScheduled = piece.slot?.use_platform_scheduling === true
    const lockToPublic = isVisibility && platformScheduled

    return (
      <div className="space-y-2">
        {label}
        <SavedField visible={savedKey === field.name}>
          <Select
            id={id}
            value={(value as string) ?? field.options[0]}
            onChange={(e) => onChange(e.target.value)}
            disabled={!editable}
          >
            {field.options.map((opt) => {
              const disabled = lockToPublic && opt !== "public"
              return (
                <option key={opt} value={opt} disabled={disabled}>
                  {opt}{disabled ? " — requires immediate publish" : ""}
                </option>
              )
            })}
          </Select>
        </SavedField>
        {isVisibility && (
          <p className="text-xs text-ink-muted">
            {lockToPublic
              ? "Locked to public while this piece is scheduled with YouTube's scheduler. Unschedule to switch."
              : "YouTube can only schedule public videos (uploaded private, flipped to public at publish time). Pick unlisted or private only for immediate uploads."}
          </p>
        )}
      </div>
    )
  }

  if (field.multiline) {
    const text       = (value as string) ?? ""
    const charCount  = text.length
    const limit      = channel.character_limit
    const showCount  = field.name === "content" && limit != null
    const overLimit  = limit != null && charCount > limit
    return (
      <div className="space-y-2">
        {label}
        <SavedField visible={savedKey === field.name}>
          <AutoGrowTextarea
            id={id}
            className="min-h-48 text-sm"
            value={text}
            onChange={(e) => onChange(e.target.value)}
            readOnly={!editable}
            placeholder="Line breaks are preserved."
          />
        </SavedField>
        {showCount && (
          <p className={cn("text-xs", overLimit ? "text-danger-display" : "text-ink-muted")}>
            {charCount} / {limit} chars
          </p>
        )}
      </div>
    )
  }

  // Single-line text (subject, preheader, title, subheadline,
  // promo_headline, excerpt, tags, …).
  return (
    <div className="space-y-2">
      {label}
      <SavedField visible={savedKey === field.name}>
        <Input
          id={id}
          value={(value as string) ?? ""}
          onChange={(e) => onChange(e.target.value)}
          readOnly={!editable}
        />
      </SavedField>
    </div>
  )
}

// Lightweight preview row for the BM article's single featured image.
// Shows a thumbnail + filename + a delete button so the operator can clear it;
// the FileUploadButton above this component handles replacement.
function FeaturedImagePreview({
  pieceId,
  attachment,
  editable,
}: {
  pieceId: number
  attachment: { id: number; filename: string | null; url: string | null }
  editable: boolean
}) {
  const remove = () => {
    if (!window.confirm("Remove the featured image?")) return
    router.delete(`/pieces/${pieceId}/attachments/${attachment.id}`, { preserveScroll: true })
  }
  return (
    <div className="flex items-center gap-2 rounded-md border border-hairline bg-page p-2">
      {attachment.url && (
        <img
          src={attachment.url}
          alt={attachment.filename ?? "Featured image"}
          className="h-9 w-9 shrink-0 rounded object-cover"
        />
      )}
      <span className="flex-1 truncate text-sm" title={attachment.filename ?? "Featured image"}>
        {attachment.filename ?? "Featured image"}
      </span>
      <AttachmentViewer url={attachment.url} filename={attachment.filename} kind="featured_image" />
      {editable && (
        <Button variant="ghost" size="sm" onClick={remove} aria-label="Remove featured image">
          Remove
        </Button>
      )}
    </div>
  )
}

// Banner shown above the content fields once a piece can no longer be
// edited — it has shipped (published), been archived, or is mid-publish
// (the kill window). Explains why the inputs are read-only so the operator
// isn't left wondering why their typing won't take.
function LockedContentNotice({ piece }: { piece: Piece }) {
  const inFlight = piece.slot?.publishing_at != null
  const reason =
    inFlight                                   ? "This piece is being published right now — its content is locked until the publish finishes." :
    piece.publishing_status === "published"    ? "This piece has been published, so its content is locked." :
    piece.publishing_status === "archived"     ? "This piece is archived. Unarchive it to edit the content." :
                                                 "This piece's content is locked."
  return (
    <p className="mb-6 rounded-md border border-hairline bg-surface px-3 py-2 text-sm text-ink-muted">
      {reason}
    </p>
  )
}

// (NewsletterContentFields / ChannelSpecificFields / TagsField were
// removed when the per-channel-type field schema moved to the server-
// driven `channel.fields_schema`. The FieldRenderer above handles every
// channel uniformly.)

// Composite widget that wraps the three newsletter promo_* fields
// (promo_headline, promo_content, promo_skip) in a single dropdown
// with three modes: Default / Custom / Don't show.
//
// The mode is derived from the underlying fields — it isn't stored as
// its own attribute. Default = nothing set + skip false; Custom =
// either headline or content set, skip false; Don't show = skip true.
// When the user changes the dropdown we reset the underlying fields so
// the derived mode follows.
type PromoMode = "default" | "custom" | "none"

function NewsletterPromoBox({
  pieceId,
  fields,
  setField,
  editable,
  savedKey,
}: {
  pieceId: number
  fields: Record<string, unknown>
  setField: (name: string, value: unknown) => void
  editable: boolean
  savedKey: string | null
}) {
  const promoHeadline = (fields.promo_headline as string) ?? ""
  const promoContent  = (fields.promo_content as string)  ?? ""
  const promoSkip     = fields.promo_skip === true

  const mode: PromoMode =
    promoSkip                                          ? "none"   :
    (promoHeadline.length > 0 || promoContent.length > 0) ? "custom" :
                                                            "default"

  const setMode = (next: PromoMode) => {
    if (next === "default") {
      setField("promo_skip",     false)
      setField("promo_headline", "")
      setField("promo_content",  "")
    } else if (next === "custom") {
      // Don't auto-populate headline/content — the operator will type
      // their own. Just clear the skip flag and reveal the inputs.
      setField("promo_skip", false)
    } else {
      // "none" — suppress for this email. Clear any custom values so
      // re-selecting "default" later doesn't surprise the operator.
      setField("promo_skip",     true)
      setField("promo_headline", "")
      setField("promo_content",  "")
    }
  }

  return (
    <div className="space-y-2">
      <label htmlFor="pe-promo-mode">Promo box</label>
      <SavedField visible={savedKey === "promo_skip"}>
        <Select
          id="pe-promo-mode"
          value={mode}
          onChange={(e) => setMode(e.target.value as PromoMode)}
          disabled={!editable}
        >
          <option value="default">Use default promo box content</option>
          <option value="custom">Custom promo box content…</option>
          <option value="none">Don&apos;t show promo box on this email</option>
        </Select>
      </SavedField>

      {mode === "custom" && (
        <div className="space-y-3 pt-2">
          <div className="space-y-2">
            <label htmlFor="pe-promo-headline">Promo headline</label>
            <SavedField visible={savedKey === "promo_headline"}>
              <Input
                id="pe-promo-headline"
                value={promoHeadline}
                onChange={(e) => setField("promo_headline", e.target.value)}
                readOnly={!editable}
                placeholder="Leave blank to use the channel's default headline"
              />
            </SavedField>
          </div>
          <div className="space-y-2">
            <label htmlFor="pe-promo-content">Promo content</label>
            <SavedField visible={savedKey === "promo_content"}>
              <RichTextField
                key={`promo-content-${pieceId}`}
                defaultValue={promoContent}
                onChange={(md) => setField("promo_content", md)}
                readOnly={!editable}
                placeholder="Write the promo content in markdown…"
                className="min-h-32"
              />
            </SavedField>
          </div>
        </div>
      )}
    </div>
  )
}

// --- Tab: Activity -----------------------------------------------------

function ActivityTab({ piece }: { piece: Piece }) {
  const composerRef = useRef<HTMLDivElement>(null)
  const jumpToComposer = () => {
    const node = composerRef.current
    if (!node) return
    node.scrollIntoView({ behavior: "smooth", block: "center" })
    // Crepe mounts a contenteditable `.ProseMirror`. Focus it after the
    // smooth scroll settles so the caret lands ready for typing.
    window.setTimeout(() => {
      const editable = node.querySelector<HTMLElement>(".ProseMirror")
      editable?.focus()
    }, 400)
  }

  return (
    <div>
      <div className="mb-10 flex items-center justify-between gap-3">
        <h2>Activity</h2>
        <Button type="button" size="sm" variant="secondary" onClick={jumpToComposer}>
          <Plus className="h-3.5 w-3.5" /> Comment
        </Button>
      </div>

      <ActivityFeed
        events={piece.events}
        eventsPath={`/pieces/${piece.id}/comments`}
        renderSummary={(event) => <PieceEventSummary event={event} />}
        emptyMessage="No comments yet. Drop one to leave a note on this piece."
      />

      <div ref={composerRef} className="mt-10">
        <ActivityComposer eventsPath={`/pieces/${piece.id}/comments`} />
      </div>
    </div>
  )
}

// Per-row summary on the piece activity feed: "Agent commented" / "You
// commented" with the actor in bold body text.
function PieceEventSummary({ event }: { event: ActivityEvent }) {
  if (event.event_type === "commented") {
    return (
      <span>
        <strong className="text-ink-display">{event.author_label}</strong>{" "}
        commented
      </span>
    )
  }
  return (
    <span>
      <strong className="text-ink-display">{event.author_label}</strong>{" "}
      {event.event_type.replace("_", " ")}
    </span>
  )
}
